import { Action } from 'redux';
import { isDataFreshProto } from '@fullstory/searchiecache';
import { MatchingUserAppCacheStatus as Status } from './types';
import { QueryCacheOptions } from '@fullstory/proto/fs/services/alexa';
import { timestamp } from '@fullstory/proto/google/protobuf';

export type CacheState<D> = {
  status: Status;
  shownData: D | null;
  newData: D | null;
};

interface SwapAction<T> extends Action<T> {
  now: Date;
}
interface UpdateAction<T, D> extends Action<T> {
  data: D;
  loading: boolean;
  error: boolean;
  now: Date;
}

type SearchieCacheActions<U, D, S> = UpdateAction<U, D> | SwapAction<S>;

interface searchieCacheInfo {
  computedAt: timestamp | null;
  cacheOptions: QueryCacheOptions | null;
}

export const createSearchieCacheReducer =
  <U extends string, S extends string, D>(
    updateType: U,
    swapType: S,
    getInfo: (d: D) => searchieCacheInfo,
    shouldSwap: (newData: D | null, shownData: D | null) => boolean,
  ) =>
  (
    state: CacheState<D> = { shownData: null, newData: null, status: Status.initial },
    action: SearchieCacheActions<U, D, S>,
  ): CacheState<D> => {
    switch (action.type) {
      case updateType: {
        const { data, error, loading, now } = action as UpdateAction<U, D>;
        if (!loading && !error && !data) return state;
        const { computedAt, cacheOptions } = getInfo(data);
        switch (state.status) {
          case Status.initial: {
            if (loading) {
              return { ...state, status: Status.isLoading };
            } else if (data) {
              // This happens when we switch from/to detail view where there's already some data in the reducer
              if (isDataFreshProto(cacheOptions, computedAt, now)) {
                return { shownData: data, newData: data, status: Status.fresh };
              } else {
                return { shownData: data, newData: null, status: Status.middle };
              }
            } else {
              return { ...state, status: Status.loadFailed };
            }
          }
          case Status.isLoading: {
            if (error) {
              return { ...state, status: Status.loadFailed };
            } else if (data) {
              if (isDataFreshProto(cacheOptions, computedAt, now)) {
                return { shownData: data, newData: data, status: Status.fresh };
              } else {
                return { shownData: data, newData: null, status: Status.middle };
              }
            } else {
              console.error('Invalid state and action', state, action);
              return state;
            }
          }
          case Status.middle: {
            if (loading) {
              return { ...state, status: Status.isRefetching };
            } else if (isDataFreshProto(cacheOptions, computedAt, now)) {
              return { shownData: data, newData: data, status: Status.fresh };
            } else {
              return { shownData: data, newData: null, status: Status.middle };
            }
          }
          case Status.isRefetching: {
            if (error) {
              return { ...state, status: Status.loadFailed };
            } else if (data) {
              if (shouldSwap(data, state.shownData)) {
                return { shownData: data, newData: data, status: Status.fresh };
              }
              return { ...state, newData: data, status: Status.hasData };
            } else {
              console.error('Invalid state and action', state, action);
              return state;
            }
          }
          case Status.loadFailed: {
            if (loading) {
              // Retry button clicked
              return { ...state, status: Status.isLoading };
            } else {
              console.error('Invalid state and action', state, action);
              return state;
            }
          }
          case Status.fresh:
            if (loading) {
              // Refresh button clicked or Scope/Settings changed
              return { ...state, status: Status.isLoading };
            } else if (data) {
              // This happens when there are two responses coming back at almost same time.
              if (isDataFreshProto(cacheOptions, computedAt, action.now)) {
                return { shownData: data, newData: data, status: Status.fresh };
              } else {
                return { shownData: data, newData: null, status: Status.middle };
              }
            } else {
              console.error('Invalid state and action', { ...state }, { ...action });
              return state;
            }
          case Status.hasData:
            if (loading) {
              // Refresh button clicked or Scope/Settings changed
              return { ...state, status: Status.isLoading };
            } else if (data) {
              // This happens when there are two responses coming back at almost same time.
              if (isDataFreshProto(cacheOptions, computedAt, action.now)) {
                return { ...state, newData: data, status: Status.hasData };
              } else {
                return { shownData: data, newData: null, status: Status.middle };
              }
            } else {
              console.error('Invalid state and action', { ...state }, { ...action });
              return state;
            }
          default: {
            return state;
          }
        }
      }
      case swapType: {
        return {
          ...state,
          shownData: state.newData,
          status: Status.fresh,
        };
      }
      default: {
        return state;
      }
    }
  };
